import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export const useArticleDraftStore = defineStore('articleDraft', () => {
    // 定时器引用
    const autoSaveTimer = ref(null)

    // 当前缓存的文章数据
    const currentDraft = ref(null)

    // 缓存类型：'create' 或 'edit'
    const draftType = ref(null)

    // 编辑文章的ID（仅编辑模式）
    const editingArticleId = ref(null)

    // 计算缓存键
    const cacheKey = computed(() => {
        if (draftType.value === 'create') {
            return 'article_create_draft'
        } else if (draftType.value === 'edit' && editingArticleId.value) {
            return `article_edit_draft_${editingArticleId.value}`
        }
        return null
    })

    /**
     * 获取本地缓存
     */
    const getLocalCache = (key) => {
        try {
            const cached = localStorage.getItem(key)
            return cached ? JSON.parse(cached) : null
        } catch (error) {
            console.error('获取本地缓存失败:', error)
            return null
        }
    }

    /**
     * 保存到本地缓存
     */
    const saveToLocalCache = (key, data) => {
        try {
            const cacheData = {
                ...data,
                timestamp: Date.now(),
                lastModified: new Date().toISOString()
            }
            localStorage.setItem(key, JSON.stringify(cacheData))
            console.log('缓存已保存:', key)
        } catch (error) {
            console.error('保存本地缓存失败:', error)
        }
    }

    /**
     * 删除本地缓存
     */
    const removeLocalCache = (key) => {
        try {
            localStorage.removeItem(key)
            console.log('缓存已清理:', key)
        } catch (error) {
            console.error('清理本地缓存失败:', error)
        }
    }

    /**
     * 初始化草稿缓存
     */
    const initDraft = (type, articleId = null) => {
        draftType.value = type
        editingArticleId.value = articleId

        // 检查是否有现有缓存
        const key = type === 'create' ? 'article_create_draft' : `article_edit_draft_${articleId}`
        const existingCache = getLocalCache(key)

        return existingCache
    }

    /**
     * 开始自动保存
     */
    const startAutoSave = (getArticleData) => {
        // 清除现有定时器
        if (autoSaveTimer.value) {
            clearInterval(autoSaveTimer.value)
        }

        // 每分钟保存一次
        autoSaveTimer.value = setInterval(async () => {
            if (cacheKey.value && typeof getArticleData === 'function') {
                try {
                    const articleData = await getArticleData()
                    if (articleData && hasContentChanged(articleData)) {
                        saveToLocalCache(cacheKey.value, articleData)
                        currentDraft.value = articleData
                        console.log('自动保存完成:', cacheKey.value)
                    }
                } catch (error) {
                    console.error('自动保存失败:', error)
                }
            }
        }, 30000) // 30秒

        console.log('自动保存已启动，每30秒保存一次')
    }

    /**
     * 停止自动保存
     */
    const stopAutoSave = () => {
        if (autoSaveTimer.value) {
            clearInterval(autoSaveTimer.value)
            autoSaveTimer.value = null
            console.log('自动保存已停止')
        }
    }

    /**
     * 手动保存草稿
     */
    const saveDraft = (articleData) => {
        if (cacheKey.value && articleData) {
            saveToLocalCache(cacheKey.value, articleData)
            currentDraft.value = articleData
        }
    }

    /**
     * 清理当前草稿缓存
     */
    const clearCurrentDraft = () => {
        if (cacheKey.value) {
            removeLocalCache(cacheKey.value)
        }

        // 停止自动保存
        stopAutoSave()

        // 重置状态
        currentDraft.value = null
        draftType.value = null
        editingArticleId.value = null
    }

    /**
     * 检查内容是否发生变化
     */
    const hasContentChanged = (newData) => {
        if (!currentDraft.value) {
            return true // 第一次保存
        }

        // 比较关键字段
        const fieldsToCompare = ['title', 'content', 'excerpt', 'cover_image_url', 'category_id', 'tag_ids', 'isPublic', 'article_type']

        return fieldsToCompare.some(field => {
            const oldValue = currentDraft.value[field]
            const newValue = newData[field]

            // 对数组进行深度比较
            if (Array.isArray(oldValue) && Array.isArray(newValue)) {
                return JSON.stringify(oldValue) !== JSON.stringify(newValue)
            }

            return oldValue !== newValue
        })
    }

    /**
     * 获取缓存的格式化时间
     */
    const getFormattedCacheTime = (cacheData) => {
        if (!cacheData || !cacheData.lastModified) {
            return '未知时间'
        }

        const date = new Date(cacheData.lastModified)
        return date.toLocaleString('zh-CN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    /**
     * 检查是否有可恢复的缓存
     */
    const hasRecoverableCache = (type, articleId = null) => {
        const key = type === 'create' ? 'article_create_draft' : `article_edit_draft_${articleId}`
        const cache = getLocalCache(key)
        return cache && (cache.title || cache.content)
    }

    return {
        // 状态
        currentDraft,
        draftType,
        editingArticleId,
        cacheKey,

        // 方法
        initDraft,
        startAutoSave,
        stopAutoSave,
        saveDraft,
        clearCurrentDraft,
        hasContentChanged,
        getFormattedCacheTime,
        hasRecoverableCache,
        getLocalCache,
        saveToLocalCache,
        removeLocalCache
    }
}) 